import React, { useEffect, useState } from 'react';

import { useToggle } from 'ui-components/hooks/use-toggle';

import { ShippingServiceRateRecommendation } from './shipping-service-rate-recommendation';
import { InsuranceProviderRateRecommendation } from './insurance-provider-rate-recommendation';
import { useInsuranceRateRecommendation } from '../use-insurance-rate-recommendation';
import { useFulfillmentPlannerContext } from '../../context';
import { useShippingServiceRecommendation } from '../use-shipping-service-recommendation';
import { useEnableSwitchAndSaveRateRecommendations } from '../../../feature-flags/hooks/use-enable-switch-and-save-rate-recommendations';
import { useAlternativeRatesRecommendation } from '../use-alternative-rates-recommendation';
import { useRateShopperRecommendation } from '../rate-shopper/use-rate-shopper-recommendation';
import { RateShopperRecommendationBanner } from './rate-shopper/rate-shopper-recommendation-banner';
import { useEnableAlternativeRatesRateShopperRecommendations } from '../../../feature-flags/hooks/use-enable-alternative-rates-rate-shopper-recommendations';

/**
 * @param {object} props
 * @param {string} props.fulfillmentPlanId
 * @param {string} [props.className]
 * @param {'light'|'medium'|'dark'} [props.theme]
 * @returns {React.JSX.Element | null}
 */
export const FulfillmentPlanRateRecommendations = ({ fulfillmentPlanId, className, theme = 'light' }) => {
  const isSwitchAndSaveRecommendationsEnabled = useEnableSwitchAndSaveRateRecommendations();
  if (!isSwitchAndSaveRecommendationsEnabled) {
    return null;
  }

  return <FulfillmentPlanRateRecommendationsContent className={className} fulfillmentPlanId={fulfillmentPlanId} theme={theme} />;
};

const FulfillmentPlanRateRecommendationsContent = ({ fulfillmentPlanId, className, theme }) => {
  const [shouldShowInsuranceRecommendation, setShouldShowInsuranceRecommendation] = useState(false);
  const { isActivated: hasDismissedInsuranceRecommendation, activate: dismissInsuranceRecommendation } = useToggle(false);

  const [shouldShowRateShopperRecommendation, setShouldShowRateShopperRecommendation] = useState(false);
  const { isActivated: hasDismissedRateShopperRecommendation, activate: dismissRateShopperRecommendation } = useToggle(false);

  const [shouldShowServiceRecommendation, setShouldShowServiceRecommendation] = useState(false);
  const { isActivated: hasDismissedServiceRecommendation, activate: dismissServiceRecommendation } = useToggle(false);

  const alternativeRatesRecommendation = useAlternativeRatesRecommendation(fulfillmentPlanId);

  const isRateShopperRecommendationsEnabled = useEnableAlternativeRatesRateShopperRecommendations();

  const { isRating } = useFulfillmentPlannerContext();

  useEffect(() => {
    if (isRating) {
      setShouldShowInsuranceRecommendation(false);
      setShouldShowServiceRecommendation(false);
      if (isRateShopperRecommendationsEnabled) {
        setShouldShowRateShopperRecommendation(false);
      }
    }
  }, [isRateShopperRecommendationsEnabled, isRating]);

  const { insuranceRateDifference, applyInsuranceRecommendation, insuranceProviderId } = useInsuranceRateRecommendation(
    fulfillmentPlanId,
    setShouldShowInsuranceRecommendation,
    alternativeRatesRecommendation,
  );

  const { applyRateShopperRecommendation, rateShopperRecommendationSavings } = useRateShopperRecommendation(
    fulfillmentPlanId,
    setShouldShowRateShopperRecommendation,
    alternativeRatesRecommendation,
  );

  const { rateDifference, onApplyRec, shippingService, isComparingBYOAToWallet } = useShippingServiceRecommendation(
    fulfillmentPlanId,
    setShouldShowServiceRecommendation,
    alternativeRatesRecommendation,
  );

  // Priority order: Insurance > Rate Shopper > Service.
  // If multiple recommendations exist, show the highest priority one first. When the user applies or dismisses it,
  // show the next recommendation.
  if (shouldShowInsuranceRecommendation && !hasDismissedInsuranceRecommendation) {
    return (
      <InsuranceProviderRateRecommendation
        className={className}
        insuranceProviderId={insuranceProviderId}
        onApply={applyInsuranceRecommendation}
        onDismiss={dismissInsuranceRecommendation}
        rateDifference={insuranceRateDifference}
        theme={theme}
      />
    );
  }

  if (isRateShopperRecommendationsEnabled && shouldShowRateShopperRecommendation && !hasDismissedRateShopperRecommendation) {
    return (
      <RateShopperRecommendationBanner
        className={className}
        onApply={applyRateShopperRecommendation}
        onDismiss={dismissRateShopperRecommendation}
        savings={rateShopperRecommendationSavings}
        theme={theme}
      />
    );
  }

  if (shouldShowServiceRecommendation && !hasDismissedServiceRecommendation) {
    return (
      <ShippingServiceRateRecommendation
        className={className}
        fulfillmentPlanId={fulfillmentPlanId}
        isComparingBYOAToWallet={isComparingBYOAToWallet}
        onApplyRec={onApplyRec}
        onDismiss={dismissServiceRecommendation}
        rateDifference={rateDifference}
        shippingService={shippingService}
        theme={theme}
      />
    );
  }

  return null;
};
